import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

const useSharedStyles = makeStyles((theme) => ({
  metricCard: {
    borderRadius: 'var(--df-radius-lg)',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)',
    background: 'var(--df-surface)',
    overflow: 'hidden'
  },
  metricEyebrow: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--df-ink-3)'
  },
  metricValue: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 26,
    fontWeight: 600,
    lineHeight: 1.1,
    color: 'var(--df-ink)',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.75)
  },
  metricCaption: {
    fontSize: 13,
    color: 'var(--df-ink-3)'
  },
  stateCard: {
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  stateCardNeutral: {
    background: 'var(--df-surface)'
  },
  stateCardDanger: {
    background: 'var(--df-danger-bg)',
    borderColor: 'var(--df-danger-text)'
  },
  stateCardMuted: {
    background: 'var(--df-surface)',
    borderColor: 'var(--df-line-2)'
  },
  stateContent: {
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(4)
  },
  stateTitle: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  stateMessage: {
    maxWidth: 560,
    color: 'var(--df-ink-3)'
  }
}))

const STATUS_STYLES = {
  overdue: { backgroundColor: 'var(--df-danger-bg)', color: 'var(--df-danger-text)', border: '1px solid var(--df-danger-text)' },
  never_done: { backgroundColor: 'var(--df-warn-bg)', color: 'var(--df-warn-text)', border: '1px solid var(--df-warn-text)' },
  due_soon: { backgroundColor: 'var(--df-warn-bg)', color: 'var(--df-warn-text)', border: '1px solid var(--df-warn-text)' },
  needs_completion: { backgroundColor: 'var(--df-purple-bg)', color: 'var(--df-purple-text)', border: '1px solid var(--df-purple-text)' },
  ok: { backgroundColor: 'var(--df-ok-bg)', color: 'var(--df-ok-text)', border: '1px solid var(--df-ok-text)' },
  ignored: { backgroundColor: 'var(--df-muted-bg)', color: 'var(--df-muted-text)', border: '1px solid var(--df-line-2)' }
}

const ACCESS_STATUS_META = {
  active: { label: 'Active', style: { backgroundColor: 'var(--df-ok-bg)', color: 'var(--df-ok-text)', border: '1px solid var(--df-ok-text)' } },
  invited: { label: 'Invited', style: { backgroundColor: 'var(--df-info-bg)', color: 'var(--df-info-text)', border: '1px solid var(--df-info-text)' } },
  suspended: { label: 'Suspended', style: { backgroundColor: 'var(--df-warn-bg)', color: 'var(--df-warn-text)', border: '1px solid var(--df-warn-text)' } },
  offboarded: { label: 'Offboarded', style: { backgroundColor: 'var(--df-danger-bg)', color: 'var(--df-danger-text)', border: '1px solid var(--df-danger-text)' } }
}

function isValidDate (value) {
  if (!value) {
    return false
  }

  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

export function formatPortalDate (value, options = {}) {
  const {
    fallback = 'Not scheduled',
    includeWeekday = false
  } = options

  if (!isValidDate(value)) {
    return fallback
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeWeekday ? { weekday: 'short' } : {})
  }).format(new Date(value))
}

export function formatPortalDateTime (value, options = {}) {
  const { fallback = 'No activity recorded' } = options

  if (!isValidDate(value)) {
    return fallback
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}

export function getStatusChipStyle (status) {
  return STATUS_STYLES[status] || { backgroundColor: 'var(--df-muted-bg)', color: 'var(--df-muted-text)' }
}

const STATUS_CHIP_CLASS = {
  overdue: 'df-chip-danger',
  never_done: 'df-chip-warn',
  due_soon: 'df-chip-warn',
  needs_completion: 'df-chip-warn',
  ok: 'df-chip-ok',
  ignored: ''
}

export function getStatusChipClass (status) {
  return STATUS_CHIP_CLASS[status] || ''
}

export function getAccessStatusMeta (status) {
  return ACCESS_STATUS_META[status] || {
    label: status ? status.replace(/_/g, ' ') : 'Unknown',
    style: { backgroundColor: 'var(--df-muted-bg)', color: 'var(--df-muted-text)' }
  }
}

export function PortalMetricCard ({ eyebrow, value, caption, borderColor = 'var(--df-line-2)' }) {
  const classes = useSharedStyles()

  return (
    <Card className={classes.metricCard} style={{ borderLeft: `3px solid ${borderColor}` }}>
      <CardContent style={{ padding: '16px 20px' }}>
        <Typography className={classes.metricEyebrow}>{eyebrow}</Typography>
        <Typography className={classes.metricValue}>
          {value}
        </Typography>
        <Typography variant="body2" className={classes.metricCaption}>
          {caption}
        </Typography>
      </CardContent>
    </Card>
  )
}

export function PortalStateCard ({
  title,
  message,
  actionLabel,
  onAction,
  tone = 'neutral',
  minHeight = 180,
  children
}) {
  const classes = useSharedStyles()
  const toneClass = tone === 'danger'
    ? classes.stateCardDanger
    : tone === 'muted'
      ? classes.stateCardMuted
      : classes.stateCardNeutral

  return (
    <Card className={`${classes.stateCard} ${toneClass}`}>
      <CardContent className={classes.stateContent} style={{ minHeight }}>
        <Typography variant="h6" className={classes.stateTitle}>
          {title}
        </Typography>
        {message ? (
          <Typography variant="body2" className={classes.stateMessage}>
            {message}
          </Typography>
        ) : null}
        {children}
        {actionLabel && onAction ? (
          <Box mt={1}>
            <Button variant="outlined" color="primary" onClick={onAction}>
              {actionLabel}
            </Button>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function PortalLoadingCard ({
  title = 'Loading portal data',
  message = 'Pulling the latest subcontractor information now.',
  minHeight = 240
}) {
  return (
    <PortalStateCard title={title} message={message} tone="muted" minHeight={minHeight}>
      <CircularProgress size={28} />
    </PortalStateCard>
  )
}

// ── Shared table styles hook ─────────────────────────────────────
export const usePortalTableStyles = makeStyles((theme) => ({
  controlsCard: {
    borderRadius: 'var(--df-radius-md)',
    marginBottom: theme.spacing(3),
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  tableCard: {
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)',
    overflow: 'hidden'
  },
  tableHeadCell: {
    fontFamily: 'var(--df-font-mono)',
    fontWeight: 600,
    fontSize: 10.5,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--df-ink-3)',
    backgroundColor: 'var(--df-surface-2)'
  },
  primaryText: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  secondaryText: {
    color: 'var(--df-ink-3)'
  },
  cardTitle: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  helperText: {
    color: 'var(--df-ink-3)'
  }
}))

// ── Color Derivation ──────────────────────────────────────────────

function hexToHsl (hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
}

export function isValidHex (str) {
  return /^#[0-9a-fA-F]{6}$/.test(str)
}

export function derivePortalTheme (primaryColor) {
  const fallback = '#1f5e3a'
  const color = isValidHex(primaryColor) ? primaryColor : fallback

  const [, , l] = hexToHsl(color)
  const isLight = l > 45
  const textOnPrimary = isLight ? '#1a1a1a' : '#ffffff'
  const textOnPrimaryMuted = isLight ? 'rgba(26,26,26,0.7)' : 'rgba(255,255,255,0.7)'

  return {
    primary: color,
    textOnPrimary,
    textOnPrimaryMuted,
    indicatorColor: textOnPrimary,
    buttonBg: color,
    buttonText: textOnPrimary
  }
}
